/**
 * Template variables for `prompts.*` values `{ text: "..." }` — substituted server-side with Python
 * `string.Template` (e.g. `$member_name`). `responses.*` messages are plain strings (no `$` substitution).
 * Used by Admin UI to show insert chips per key.
 */
export interface PromptVarMeta {
  name: string;
  /** Short hint for admins */
  hint: string;
}

const COMMON: PromptVarMeta[] = [
  { name: 'ui_language', hint: 'Limba răspunsului (română / engleză)' },
  { name: 'member_name', hint: 'Nume membru' },
  { name: 'member_label', hint: 'Etichetă membru (context căutare)' },
  { name: 'user_message', hint: 'Mesaj utilizator' },
  { name: 'context', hint: 'Bloc documente pentru răspuns' },
  { name: 'document', hint: 'Conținut document (rezumat)' },
  { name: 'combined', hint: 'Rezumate parțiale (reduce)' },
  { name: 'chunk', hint: 'Fragment document (map)' },
  { name: 'chunk_index', hint: 'Index secțiune' },
  { name: 'num_chunks', hint: 'Număr total secțiuni' },
  { name: 'docs_text', hint: 'Text documente (insights — legacy prose)' },
  { name: 'documents_payload', hint: 'Documente cu document_id (cronologie JSON)' },
  { name: 'retrieval_summary', hint: 'Rezumat documente top (planificator / validator)' },
  { name: 'conversation', hint: 'Istoric conversație (chat agentic)' },
  { name: 'focus_mode', hint: 'Mod focalizare (cheia activă din admin)' },
  { name: 'draft_answer', hint: 'Răspuns propus (validator)' },
  // Classification template placeholders — substituted at runtime by
  // `app.services.classification_prompt_builder` against the live
  // taxonomy (classifiers + entries + links). Editable in Admin →
  // Clasificări.
  { name: 'json_structure', hint: 'Scheletul JSON complet așteptat de la LLM (auto)' },
  { name: 'preferred_values', hint: 'Valorile preferate pe clasificator (auto)' },
  { name: 'relations', hint: 'Regulile „dacă alegi X, alege și Y" între clasificatori (auto)' },
  // Context-card / chunk-answer / chunk-summarizer / planner placeholders
  // (new prompts seeded by W2). Surface them on the relevant rows so admins
  // get insertion chips without hunting in code.
  { name: 'filename', hint: 'Numele fișierului documentului' },
  { name: 'document_id', hint: 'Identificatorul documentului curent' },
  { name: 'document_date', hint: 'Data documentului (ISO)' },
  { name: 'category', hint: 'Categorie / tip principal al documentului' },
  { name: 'content', hint: 'Conținutul textual al documentului (markdown / OCR)' },
  { name: 'ocr_text', hint: 'Textul OCR trunchiat' },
  { name: 'structured_extracts', hint: 'Extrase structurate din clasificare' },
  // LangChain / tool planner + chunk-answer placeholders (W2 templates).
  { name: 'question', hint: 'Întrebarea utilizatorului (canonică)' },
  { name: 'intent_json', hint: 'Intenția de căutare (JSON)' },
  { name: 'retrieval_note', hint: 'Notă scurtă despre rezultatele căutării' },
  { name: 'tried_json', hint: 'Sub-întrebări deja încercate (JSON)' },
  { name: 'remaining_iterations', hint: 'Câte iterații mai sunt permise' },
  { name: 'max_subqueries', hint: 'Plafon pentru sub-întrebări (planner)' },
  { name: 'max_actions', hint: 'Plafon pentru acțiuni de planificare' },
];

/** Variables most likely used per config key (subset of COMMON + key-specific). */
const BY_KEY: Record<string, string[]> = {
  'prompts.intent.user': ['member_label', 'user_message', 'ui_language'],
  'prompts.answer.system': ['ui_language'],
  'prompts.answer.user': ['member_name', 'user_message', 'context', 'ui_language'],
  'prompts.agent_planner.user': [
    'member_label',
    'user_message',
    'retrieval_summary',
    'conversation',
    'focus_mode',
    'ui_language',
  ],
  'prompts.answer_agentic.system': ['ui_language', 'focus_mode'],
  'prompts.answer_agentic.user': [
    'member_name',
    'user_message',
    'conversation',
    'focus_mode',
    'context',
    'ui_language',
  ],
  'prompts.answer_validator.user': ['user_message', 'retrieval_summary', 'draft_answer', 'ui_language'],
  'prompts.summary.system': ['ui_language'],
  'prompts.timeline.system': ['ui_language'],
  'prompts.insights.system': ['ui_language'],
  'prompts.summary.single_user': ['document', 'ui_language'],
  'prompts.summary.map_chunk': ['chunk_index', 'num_chunks', 'chunk', 'ui_language'],
  'prompts.summary.reduce_user': ['combined', 'ui_language'],
  // Timeline now emits a structured JSON document (see
  // `backend/app/schemas/timeline.py`). The user template only stamps the
  // member name + the doc payload (document_id-prefixed markdown); the
  // system prompt encodes the full JSON contract. `ui_language` is no
  // longer substituted server-side because the system prompt locks the
  // output language to Romanian for `title` / `summary` / `highlights`.
  'prompts.timeline.user': ['member_name', 'documents_payload'],
  'prompts.insights.user': ['member_name', 'docs_text', 'ui_language'],
  'prompts.classification.system': ['json_structure', 'preferred_values', 'relations'],
  'prompts.classification.user': ['document'],
  // Chunk-level Q&A pair (single document → single chunk answer, also
  // used by the LangChain-native standard chain as the answer prompt).
  // System prompt has no $-placeholders (instructional copy only); user
  // template stamps $member_name + $question + $ui_language + $context.
  'prompts.chunk_answer.system': [],
  'prompts.chunk_answer.user': ['member_name', 'question', 'ui_language', 'context'],
  // AgentExecutor system prompt — encodes the auth invariant, weak-retrieval
  // re-planning, tool-call budget, and SOURCES_USED output contract.
  // Instructional copy only (no $-placeholders).
  'prompts.agent_loop.system': [],
  // MultiQueryRetriever paraphrase prompt — produces 3 reformulations of
  // the user's query so the retriever fans out and merges hits.
  // Instructional copy only (no $-placeholders).
  'prompts.multiquery.system': [],
};

const byName = new Map(COMMON.map((v) => [v.name, v]));

export function getPromptVarsForKey(key: string): PromptVarMeta[] {
  /* responses.* = mesaje scurte către utilizator; fără variabile $ în șablon */
  if (key.startsWith('responses.')) {
    return [];
  }
  const names = BY_KEY[key];
  if (names?.length) {
    return names.map((n) => byName.get(n) ?? { name: n, hint: n });
  }
  if (key.startsWith('prompts.')) {
    return COMMON;
  }
  return [];
}

export function insertAtCursor(
  el: HTMLTextAreaElement | null,
  text: string,
  onChange: (next: string) => void,
  current: string
): void {
  if (!el) {
    onChange(current + text);
    return;
  }
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const next = current.slice(0, start) + text + current.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    el.focus();
    const pos = start + text.length;
    el.setSelectionRange(pos, pos);
  });
}
